import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getConfig } from '../config/environment';

const config = getConfig();
const API_BASE_URL = config.apiUrl;

interface DemoChatInputProps {
  useKnowledgeBase: boolean;
  onMessageSent: (userMessage: string, aiResponse: string, sources?: any[]) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const DemoChatInput: React.FC<DemoChatInputProps> = ({ 
  useKnowledgeBase, 
  onMessageSent, 
  isLoading,
  setIsLoading 
}) => {
  const [message, setMessage] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim() || isLoading) {
      return;
    }

    const messageToSend = message.trim();
    setMessage('');
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/chat/demo`,
        {
          message: messageToSend,
          session_id: `demo-${Date.now()}`,
          use_knowledge_base: useKnowledgeBase
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      onMessageSent(
        messageToSend, 
        response.data.content,
        response.data.sources || []
      );
    } catch (error: any) {
      console.error('Failed to send message:', error);
      setMessage(messageToSend);
      onMessageSent(
        messageToSend,
        'Sorry, there was an error processing your request. Please try again.',
        []
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          maxWidth: 768,
          position: 'relative'
        }}
      >
        <TextField
          ref={inputRef}
          fullWidth
          multiline
          maxRows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          placeholder={useKnowledgeBase ? "Ask about your organization..." : "Ask a general question..."}
          disabled={isLoading}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: `${theme.palette.background.paper} !important`,
              border: `1px solid ${theme.palette.divider}`,
              paddingRight: 4,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
                backgroundColor: `${theme.palette.background.paper} !important`,
              },
              '&.Mui-focused': {
                borderColor: theme.palette.primary.main,
                boxShadow: `0 4px 12px rgba(99, 102, 241, 0.2)`,
                backgroundColor: `${theme.palette.background.paper} !important`,
              },
              '& fieldset': {
                border: 'none'
              },
              '& input': {
                color: theme.palette.text.primary,
                backgroundColor: 'transparent !important',
                '&::placeholder': {
                  color: theme.palette.text.secondary,
                  opacity: 1
                }
              },
              '& textarea': {
                color: theme.palette.text.primary,
                backgroundColor: 'transparent !important',
                '&::placeholder': {
                  color: theme.palette.text.secondary,
                  opacity: 1
                }
              }
            },
            '& .MuiInputBase-input': {
              backgroundColor: 'transparent !important',
              color: `${theme.palette.text.primary} !important`,
            },
            '& .MuiInputBase-root': {
              backgroundColor: `${theme.palette.background.paper} !important`,
            }
          }}
        />
        
        <IconButton
          type="submit"
          disabled={!message.trim() || isLoading}
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 32,
            height: 32,
            borderRadius: 2,
            backgroundColor: message.trim() 
              ? theme.palette.primary.main 
              : 'transparent',
            color: message.trim() ? 'white' : theme.palette.text.disabled,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: message.trim() 
                ? theme.palette.primary.dark 
                : theme.palette.action.hover,
              transform: message.trim() ? 'translateY(-50%) scale(1.1)' : 'translateY(-50%)'
            },
            '&:disabled': {
              backgroundColor: 'transparent',
              color: theme.palette.action.disabled
            },
          }}
        >
          <SendIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Typography 
        variant="caption" 
        sx={{ 
          color: theme.palette.text.disabled,
          fontSize: '0.75rem',
          textAlign: 'center'
        }}
      >
        {useKnowledgeBase 
          ? 'CoreSupport AI with Knowledge Base - Demo Mode'
          : 'Generic AI Assistant - Demo Mode'
        }
      </Typography>
    </Box>
  );
};

export default DemoChatInput;
